/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { 
  Car, 
  CarFront, 
  Search, 
  Edit3, 
  Trash2, 
  Image as ImageIcon, 
  FileText, 
  Download, 
  Gauge, 
  MapPin, 
  X, 
  ArrowUpDown, 
  RotateCcw,
  Plus,
  Globe,
  EyeOff
} from "lucide-react";
import toast from "react-hot-toast";
import "./stock.css";

const StockList = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ 
    search: "", 
    status: "", 
    year: "", 
    sortBy: "newest" 
  });
  
  const [globalMileageUnit, setGlobalMileageUnit] = useState("auto"); // "auto", "km", "miles"
  const [cardToggledUnits, setCardToggledUnits] = useState({});

  const toggleCardMileageUnit = (carId, currentUnit) => {
    setCardToggledUnits(prev => ({
      ...prev,
      [carId]: currentUnit === "miles" ? "km" : "miles"
    }));
  };

  const formatCarMileage = (car) => {
    if (car.mileage == null || car.mileage === "") {
      return { display: "— km", secondary: "", unit: "km" };
    }
    const num = Number(car.mileage);
    const nativeUnit = (car.mileageUnit || "km").toLowerCase();

    let activeUnit = nativeUnit;
    if (cardToggledUnits[car.id]) {
      activeUnit = cardToggledUnits[car.id];
    } else if (globalMileageUnit !== "auto") {
      activeUnit = globalMileageUnit;
    }

    let kmVal, miVal;
    if (nativeUnit === "miles" || nativeUnit === "mi") {
      miVal = num;
      kmVal = Math.round(num * 1.60934);
    } else {
      kmVal = num;
      miVal = Math.round(num * 0.621371);
    }

    if (activeUnit === "miles" || activeUnit === "mi") {
      return {
        display: `${miVal.toLocaleString()} mi`,
        secondary: `${kmVal.toLocaleString()} km`,
        unit: "miles"
      };
    } else {
      return {
        display: `${kmVal.toLocaleString()} km`,
        secondary: `${miVal.toLocaleString()} mi`,
        unit: "km"
      };
    }
  };

  const [deleteModal, setDeleteModal] = useState({ show: false, car: null });
  // Unified Media Modal for both Images and Documents
  const [mediaModal, setMediaModal] = useState({ 
    show: false, 
    type: "image", 
    url: "", 
    images: [], 
    idx: 0, 
    title: "" 
  });
  const navigate = useNavigate();

  const handleSyncSingleCar = async (carId) => {
    try {
      toast.loading("Syncing vehicle to website...", { id: `sync-${carId}` });
      const { data } = await api.post(`/cars/${carId}/sync-website`);
      if (data.websiteResponse?.success) {
        toast.success("Synced to PAJ Website!", { id: `sync-${carId}` });
      } else {
        toast.error(data.websiteResponse?.error || "Website sync response received", { id: `sync-${carId}` });
      }
    } catch (err) {
      toast.error("Website API currently unreachable (Check deployment)", { id: `sync-${carId}` });
    }
  };

  const fetchCars = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cars");
      setCars(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load car stock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchCars(); 
  }, []);

  // Compute status counts for quick segment filters
  const counts = useMemo(() => {
    return {
      all: cars.length,
      available: cars.filter(c => c.status === "Available" || !c.status).length,
      reserved: cars.filter(c => c.status === "Reserved").length,
      sold: cars.filter(c => c.status === "Sold").length,
    };
  }, [cars]);

  // Filter & sort cars
  const visible = useMemo(() => {
    let result = [...cars];

    // Search filter
    if (filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      result = result.filter(c => 
        (c.make || "").toLowerCase().includes(q) ||
        (c.model || "").toLowerCase().includes(q) ||
        (c.chassis || "").toLowerCase().includes(q) ||
        (c.lotNumber || "").toLowerCase().includes(q) ||
        (c.auctionName || "").toLowerCase().includes(q)
      );
    }

    // Status filter
    if (filters.status) {
      result = result.filter(c => c.status === filters.status);
    }

    // Year filter
    if (filters.year) {
      result = result.filter(c => c.year === Number(filters.year));
    }

    // Sorting
    if (filters.sortBy === "year_desc") {
      result.sort((a, b) => (b.year || 0) - (a.year || 0));
    } else if (filters.sortBy === "year_asc") {
      result.sort((a, b) => (a.year || 0) - (b.year || 0));
    } else if (filters.sortBy === "price_asc") {
      result.sort((a, b) => (Number(a.buyingPriceYen) || Number(a.cifPrice) || 0) - (Number(b.buyingPriceYen) || Number(b.cifPrice) || 0));
    } else if (filters.sortBy === "price_desc") {
      result.sort((a, b) => (Number(b.buyingPriceYen) || Number(b.cifPrice) || 0) - (Number(a.buyingPriceYen) || Number(a.cifPrice) || 0));
    } else if (filters.sortBy === "mileage_asc") {
      result.sort((a, b) => {
        const getKm = (c) => {
          if (c.mileage == null || c.mileage === "") return Infinity;
          const num = Number(c.mileage);
          const u = (c.mileageUnit || "km").toLowerCase();
          return (u === "miles" || u === "mi") ? num * 1.60934 : num;
        };
        return getKm(a) - getKm(b);
      });
    } else if (filters.sortBy === "mileage_desc") {
      result.sort((a, b) => {
        const getKm = (c) => {
          if (c.mileage == null || c.mileage === "") return -1;
          const num = Number(c.mileage);
          const u = (c.mileageUnit || "km").toLowerCase();
          return (u === "miles" || u === "mi") ? num * 1.60934 : num;
        };
        return getKm(b) - getKm(a);
      });
    } else {
      // Default: newest added (by ID desc)
      result.sort((a, b) => (b.id || 0) - (a.id || 0));
    }

    return result;
  }, [cars, filters]);

  const hasActiveFilters = Boolean(filters.search || filters.status || filters.year || filters.sortBy !== "newest");

  const handleResetFilters = () => {
    setFilters({ search: "", status: "", year: "", sortBy: "newest" });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/cars/${deleteModal.car.id}`);
      setCars(prev => prev.filter(c => c.id !== deleteModal.car.id));
      setDeleteModal({ show: false, car: null });
      toast.success("Vehicle deleted successfully");
    } catch (err) {
      toast.error("Failed to delete vehicle. Admin only.");
    }
  };

  const fileUrl = (path) => path?.startsWith("http") ? path : `${process.env.REACT_APP_API_URL}${path}`;

  // Force Cloudinary to download instead of open in browser
  const handleDownload = () => {
    let currentUrl = mediaModal.type === "document" ? mediaModal.url : mediaModal.images[mediaModal.idx];
    currentUrl = fileUrl(currentUrl);
    
    if (currentUrl.includes("cloudinary.com")) {
      const dlUrl = currentUrl.replace("/upload/", "/upload/fl_attachment/");
      window.location.href = dlUrl;
    } else {
      window.open(currentUrl, "_blank");
    }
  };

  const handleDownloadAll = () => {
    if (mediaModal.type !== "image" || !mediaModal.images.length) return;
    mediaModal.images.forEach((url, index) => {
      setTimeout(() => {
        let currentUrl = fileUrl(url);
        if (currentUrl.includes("cloudinary.com")) {
          currentUrl = currentUrl.replace("/upload/", "/upload/fl_attachment/");
        }
        const link = document.createElement("a");
        link.href = currentUrl;
        link.setAttribute("download", `car_photo_${index + 1}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 300);
    });
  };

  return (
    <div className="stock-page">
      {/* ── Page Header ── */}
      <div className="stock-header">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h1 className="stock-h1">Car Stock</h1>
            <span className="badge rounded-pill" style={{ 
              background: "var(--primary-light, rgba(59, 130, 246, 0.1))", 
              color: "var(--primary, #3b82f6)", 
              border: "1px solid var(--primary-border, rgba(59, 130, 246, 0.25))", 
              padding: "4px 10px", 
              fontSize: "12px", 
              fontWeight: 600 
            }}>
              {cars.length} Vehicles
            </span>
          </div>
          <div className="stock-sub">Search, inspect specifications, and manage live inventory</div>
        </div>

        <div className="stock-header-right">
          <button className="stock-btn-add" onClick={() => navigate("/dashboard/add-car")}>
            <Plus size={15} /> Add New Car
          </button>
        </div>
      </div>

      {/* ── Filter Bar & Quick Status Segments ── */}
      <div className="stock-filters-panel">
        {/* Quick Status Buttons */}
        <div className="stock-status-segments">
          <button 
            className={`stock-status-segment-btn ${filters.status === "" ? "active" : ""}`}
            onClick={() => setFilters(p => ({ ...p, status: "" }))}
          >
            All Stock <span className="stock-segment-count">{counts.all}</span>
          </button>
          <button 
            className={`stock-status-segment-btn available ${filters.status === "Available" ? "active available" : ""}`}
            onClick={() => setFilters(p => ({ ...p, status: p.status === "Available" ? "" : "Available" }))}
          >
            Available <span className="stock-segment-count">{counts.available}</span>
          </button>
          <button 
            className={`stock-status-segment-btn reserved ${filters.status === "Reserved" ? "active reserved" : ""}`}
            onClick={() => setFilters(p => ({ ...p, status: p.status === "Reserved" ? "" : "Reserved" }))}
          >
            Reserved <span className="stock-segment-count">{counts.reserved}</span>
          </button>
          <button 
            className={`stock-status-segment-btn sold ${filters.status === "Sold" ? "active sold" : ""}`}
            onClick={() => setFilters(p => ({ ...p, status: p.status === "Sold" ? "" : "Sold" }))}
          >
            Sold <span className="stock-segment-count">{counts.sold}</span>
          </button>
        </div>

        {/* Search & Select Controls */}
        <div className="stock-controls-row">
          <div className="stock-search-wrap">
            <Search size={15} className="stock-search-icon" />
            <input
              type="text"
              className="stock-search"
              placeholder="Search make, model, chassis, lot #, or auction..."
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            />
            {filters.search && (
              <button className="stock-search-clear" onClick={() => setFilters(p => ({ ...p, search: "" }))} title="Clear search">
                <X size={13} />
              </button>
            )}
          </div>

          <select 
            className="stock-filter-sel" 
            value={filters.year} 
            onChange={(e) => setFilters(p => ({ ...p, year: e.target.value }))}
          >
            <option value="">All Years</option>
            {[...new Set(cars.map((c) => c.year))].filter(Boolean).sort((a, b) => b - a).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select 
            className="stock-filter-sel" 
            value={filters.sortBy} 
            onChange={(e) => setFilters(p => ({ ...p, sortBy: e.target.value }))}
          >
            <option value="newest">Sort: Newest Added</option>
            <option value="year_desc">Sort: Year (New to Old)</option>
            <option value="year_asc">Sort: Year (Old to New)</option>
            <option value="mileage_asc">Sort: Mileage (Lowest first)</option>
            <option value="mileage_desc">Sort: Mileage (Highest first)</option>
            <option value="price_asc">Sort: Price (Low to High)</option>
            <option value="price_desc">Sort: Price (High to Low)</option>
          </select>

          <select 
            className="stock-filter-sel" 
            value={globalMileageUnit} 
            onChange={(e) => setGlobalMileageUnit(e.target.value)}
            title="Set preferred mileage unit across all cards"
          >
            <option value="auto">Unit: Auto</option>
            <option value="km">Unit: KM</option>
            <option value="miles">Unit: Miles</option>
          </select>

          {hasActiveFilters && (
            <button className="stock-filter-reset" onClick={handleResetFilters} title="Reset all filters">
              <RotateCcw size={13} /> Reset
            </button>
          )}
        </div>
      </div>

      {/* ── Cards Grid ── */}
      {loading ? (
        <div className="stock-grid" data-tour="stock-inventory">
          {[1, 2, 3, 4, 5, 6].map(n => (
            <div key={n} className="stock-card p-3">
              <div className="crm-skeleton-pulse mb-3" style={{ width: "100%", height: 180, borderRadius: 12 }} />
              <div className="crm-skeleton-pulse mb-2" style={{ width: "70%", height: 18, borderRadius: 4 }} />
              <div className="crm-skeleton-pulse mb-2" style={{ width: "90%", height: 14, borderRadius: 4 }} />
              <div className="crm-skeleton-pulse mt-auto" style={{ width: "100%", height: 32, borderRadius: 6 }} />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="stock-empty-state">
          <CarFront size={44} style={{ opacity: 0.4 }} />
          <div className="stock-empty-title">No vehicles found</div>
          <div className="small">No stock inventory matches your search criteria.</div>
          {hasActiveFilters && (
            <Button variant="outline-primary" size="sm" className="mt-2" onClick={handleResetFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="stock-grid" data-tour="stock-inventory">
          {visible.map((car) => {
            const images = car.images || [];
            const mainImg = images[0] ? fileUrl(images[0]) : null;
            const isSold = car.status === "Sold";
            const isReserved = car.status === "Reserved";
            const mileageInfo = formatCarMileage(car);

            return (
              <div key={car.id} className="stock-card">
                {/* Image Section */}
                <div 
                  className="stock-card__img" 
                  onClick={() => images.length && setMediaModal({ 
                    show: true, 
                    type: "image", 
                    images, 
                    idx: 0, 
                    title: `${car.year} ${car.make} ${car.model}` 
                  })}
                  title={images.length ? "Click to view photo gallery" : undefined}
                >
                  {mainImg ? (
                    <img src={mainImg} alt={`${car.make} ${car.model}`} className="stock-card__photo" />
                  ) : (
                    <div className="stock-card__noimg">
                      <div className="stock-card__noimg-icon-wrap">
                        <CarFront size={28} />
                      </div>
                      <span className="stock-card__noimg-label">No Photos Uploaded</span>
                    </div>
                  )}

                  {/* Glassmorphic Status Pill */}
                  <span className={`stock-card__status-badge ${isSold ? "sold" : isReserved ? "reserved" : "available"}`}>
                    <span className="stock-card__status-dot" />
                    {car.status || "Available"}
                  </span>

                  {/* Photo Counter */}
                  {images.length > 0 && (
                    <span className="stock-card__img-count">
                      <ImageIcon size={11} /> {images.length} photos
                    </span>
                  )}
                </div>

                {/* Card Body */}
                <div className="stock-card__body">
                  <h3 className="stock-card__title" title={`${car.year} ${car.make} ${car.model}`}>
                    {car.year} {car.make} {car.model}
                  </h3>

                  {/* Key Specs Bar: Chassis, Lot #, Mileage, Location */}
                  <div className="stock-card__specs-row">
                    <span className="stock-card__spec-pill chassis" title={`Chassis: ${car.chassis || "N/A"}`}>
                      {car.chassis || "N/A"}
                    </span>
                    {car.lotNumber && (
                      <span className="stock-card__spec-pill lot" title={`Auction Lot #${car.lotNumber}`}>
                        Lot #{car.lotNumber}
                      </span>
                    )}
                    <span 
                      className="stock-card__spec-pill mileage clickable" 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCardMileageUnit(car.id, mileageInfo.unit);
                      }}
                      title={mileageInfo.secondary ? `Click to toggle unit (Equivalent: ${mileageInfo.secondary})` : "Vehicle Mileage"}
                    >
                      <Gauge size={12} className="text-primary" />
                      <span>{mileageInfo.display}</span>
                    </span>
                    <span className="stock-card__spec-pill location" title={car.auctionName || (car.color ? car.color : "In Stock")}>
                      <MapPin size={11} />
                      <span className="stock-card__truncate">{car.auctionName || (car.color ? car.color : "In Stock")}</span>
                    </span>
                    {car.isPublicWebsite ? (
                      <span className="stock-card__spec-pill" style={{ color: 'var(--primary)', borderColor: 'var(--primary-border)' }} title="Published on PAJ Website">
                        <Globe size={11} />
                        <span>Website</span>
                      </span>
                    ) : (
                      <span className="stock-card__spec-pill" style={{ opacity: 0.6 }} title="Internal Only (Not on website)">
                        <EyeOff size={11} />
                        <span>Internal</span>
                      </span>
                    )}
                  </div>

                  {/* Document Micro-Pills */}
                  <div className="stock-card__docs">
                    {car.auctionSheet && (
                      <button 
                        className="stock-card__doc-badge" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setMediaModal({ show: true, type: "document", url: car.auctionSheet, title: "Auction Sheet" }); 
                        }}
                        title="View Auction Sheet"
                      >
                        <FileText size={10} /> Auction Sheet
                      </button>
                    )}
                    {car.exportCertificateUrl && (
                      <button 
                        className="stock-card__doc-badge" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setMediaModal({ show: true, type: "document", url: car.exportCertificateUrl, title: "Export Certificate" }); 
                        }}
                        title="View Export Certificate"
                      >
                        <FileText size={10} /> Exp. Cert
                      </button>
                    )}
                    {car.eaaUrl && (
                      <button 
                        className="stock-card__doc-badge" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setMediaModal({ show: true, type: "document", url: car.eaaUrl, title: "EAA Document" }); 
                        }}
                        title="View EAA Document"
                      >
                        <FileText size={10} /> EAA
                      </button>
                    )}
                  </div>

                  {/* Footer / Price & Actions */}
                  <div className="stock-card__footer">
                    <div className="stock-card__price-stack">
                      <span className="stock-card__price">
                        ¥{car.buyingPriceYen ? Number(car.buyingPriceYen).toLocaleString() : "POA"}
                      </span>
                      <span className="stock-card__price-sub">
                        CIF: ${car.cifPrice ? Number(car.cifPrice).toLocaleString() : "POA"}
                      </span>
                    </div>

                    <div className="stock-card__actions">
                      <button 
                        className="stock-action-btn sync" 
                        onClick={() => handleSyncSingleCar(car.id)}
                        title="Re-sync Vehicle to PAJ Website"
                      >
                        <Globe size={12} />
                      </button>
                      <button 
                        className="stock-action-btn edit" 
                        onClick={() => navigate(`/dashboard/add-car?edit=${car.id}`)}
                        title="Edit Vehicle Details"
                      >
                        <Edit3 size={12} /> Edit
                      </button>
                      <button 
                        className="stock-action-btn delete" 
                        onClick={() => setDeleteModal({ show: true, car })}
                        title="Delete Vehicle"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, car: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "16px", fontWeight: 700 }}>Delete Vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteModal.car?.year} {deleteModal.car?.make} {deleteModal.car?.model}</strong> (Chassis: {deleteModal.car?.chassis})?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setDeleteModal({ show: false, car: null })}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            Delete Car
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Unified Media Viewer Modal (Images & Documents) ── */}
      <Modal 
        show={mediaModal.show} 
        onHide={() => setMediaModal({ show: false, type: "image", url: "", images: [], idx: 0, title: "" })} 
        centered 
        size="xl" 
        contentClassName="bg-dark text-white border-0"
      >
        <Modal.Header className="border-secondary" closeButton closeVariant="white">
          <Modal.Title className="d-flex align-items-center w-100" style={{ fontSize: "16px", fontWeight: 600 }}>
            {mediaModal.title}
            <div className="ms-auto me-3 d-flex gap-2">
              {mediaModal.type === "image" && mediaModal.images.length > 1 && (
                <Button variant="outline-light" size="sm" className="d-flex align-items-center gap-2" onClick={handleDownloadAll}>
                  <Download size={14} /> Download All ({mediaModal.images.length})
                </Button>
              )}
              <Button variant="primary" size="sm" className="d-flex align-items-center gap-2" onClick={handleDownload}>
                <Download size={14} /> {mediaModal.type === "image" ? "Download Current" : "Download Document"}
              </Button>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 d-flex flex-column" style={{ background: "#000", height: mediaModal.type === "document" ? "85vh" : "auto" }}>
          {mediaModal.type === "document" ? (
            <iframe 
              src={fileUrl(mediaModal.url)} 
              title={mediaModal.title} 
              style={{ width: "100%", height: "100%", border: "none" }} 
            />
          ) : mediaModal.images.length > 0 && (
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: "80vh" }}>
              <img
                src={fileUrl(mediaModal.images[mediaModal.idx])}
                alt={mediaModal.title}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
              
              {/* Floating Counter */}
              {mediaModal.images.length > 1 && (
                <div style={{ position: "absolute", top: 15, left: 15, background: "rgba(0,0,0,0.6)", padding: "4px 12px", borderRadius: "20px", color: "#fff", fontSize: "13px", fontWeight: "bold" }}>
                  {mediaModal.idx + 1} / {mediaModal.images.length}
                </div>
              )}

              {/* Floating Arrow Buttons */}
              {mediaModal.images.length > 1 && (
                <>
                  <button 
                    onClick={() => setMediaModal(p => ({ ...p, idx: p.idx > 0 ? p.idx - 1 : p.images.length - 1 }))}
                    style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}
                    onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.3)"}
                    onMouseOut={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                  >
                    &#10094;
                  </button>
                  <button 
                    onClick={() => setMediaModal(p => ({ ...p, idx: p.idx < p.images.length - 1 ? p.idx + 1 : 0 }))}
                    style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}
                    onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.3)"}
                    onMouseOut={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                  >
                    &#10095;
                  </button>
                </>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StockList;
